import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, Loader2, Trash2, Upload } from "lucide-react";
import { getEmployeeDocs, uploadEmployeeDoc, openEmployeeDoc, deleteEmployeeDoc } from "@/lib/employeeApi";

const today = () => new Date().toISOString().slice(0, 10);
const expiryTag = (d) => {
  if (!d) return null;
  const days = (new Date(d) - new Date(today())) / 86400000;
  if (days < 0) return <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[10px] font-semibold">Expired</span>;
  if (days <= 30) return <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold">Expires soon</span>;
  return null;
};

const UploadRow = ({ userId, types, onDone }) => {
  const [f, setF] = useState({ file: null, doc_type: "Other", name: "", expiry: "" });
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!f.file) return toast.error("Choose a file");
    setBusy(true);
    try { await uploadEmployeeDoc(userId, f); toast.success("Document uploaded"); setF({ file: null, doc_type: "Other", name: "", expiry: "" }); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };
  return (
    <div className="grid sm:grid-cols-[1.3fr_1fr_1fr_1fr_auto] gap-2 items-end p-3 rounded-lg bg-slate-50" data-testid="user-doc-upload">
      <div><Label className="text-xs">File</Label><Input key={f.file ? "f" : "e"} type="file" className="mt-1" onChange={(e) => setF({ ...f, file: e.target.files?.[0] || null })} data-testid="user-doc-file" /></div>
      <div><Label className="text-xs">Type</Label>
        <Select value={f.doc_type} onValueChange={(v) => setF({ ...f, doc_type: v })}>
          <SelectTrigger className="mt-1" data-testid="user-doc-type"><SelectValue /></SelectTrigger>
          <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select></div>
      <div><Label className="text-xs">Name</Label><Input className="mt-1" value={f.name} placeholder="Optional" onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-doc-name" /></div>
      <div><Label className="text-xs">Expires</Label><Input type="date" className="mt-1" value={f.expiry} onChange={(e) => setF({ ...f, expiry: e.target.value })} data-testid="user-doc-expiry" /></div>
      <Button type="button" onClick={go} disabled={busy} className="bg-teal hover:bg-teal-dark text-white" data-testid="user-doc-upload-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1" />Upload</>}</Button>
    </div>
  );
};

export const DocumentsTab = ({ userId, types }) => {
  const [docs, setDocs] = useState(null);
  const load = useCallback(() => getEmployeeDocs(userId).then(setDocs).catch(() => setDocs([])), [userId]);
  useEffect(() => { load(); }, [load]);
  const remove = async (d) => {
    if (!window.confirm(`Delete ${d.name}?`)) return;
    try { await deleteEmployeeDoc(userId, d.id); load(); } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div className="space-y-4" data-testid="user-documents-tab">
      <UploadRow userId={userId} types={types} onDone={load} />
      {docs === null ? <Loader2 className="w-5 h-5 animate-spin text-teal" /> : docs.length === 0 ? <p className="text-sm text-slate-500">No documents yet.</p> : (
        <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-3 px-3 py-2" data-testid={`user-doc-${d.id}`}>
              <FileText className="w-4 h-4 text-teal shrink-0" />
              <button type="button" onClick={() => openEmployeeDoc(userId, d.id)} className="flex-1 min-w-0 text-left" data-testid={`user-doc-open-${d.id}`}>
                <p className="text-sm font-medium text-navy truncate">{d.name}</p>
                <p className="text-xs text-slate-500">{d.doc_type} · {d.size < 1024 ? `${d.size} B` : `${(d.size / 1024).toFixed(0)} KB`} · {d.uploaded_by} · {new Date(d.uploaded_at).toLocaleDateString()}{d.expiry ? ` · expires ${d.expiry}` : ""}</p>
              </button>
              {expiryTag(d.expiry)}
              <button type="button" onClick={() => remove(d)} className="text-slate-400 hover:text-red-600" data-testid={`user-doc-delete-${d.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
